import { Grid, Typography } from 'vendor/material';
import { Banner, Link } from '@tackle-io/platform-ui';
import NumberIcon from './NumberIcon';
import VendorMarketplaceAccountFormSection from '../forms/VendorMarketplaceAccountFormSection';
import VendorTackleAndMsftFormSection from '../forms/VendorTackleAndMsftFormSection';
import VendorAdditionalServicesFormSection from '../forms/VendorAdditionalServicesFormSection';
import { sumFormProgressQueryResult } from 'utils/yup-extended';
import { useVendorMarketplaceAccountFormSectionProgress } from '../forms/VendorMarketplaceAccountFormSection';
import { useVendorTackleAndMsftFormSectionProgress } from '../forms/VendorTackleAndMsftFormSection';
import { useVendorAdditionalServicesFormSectionProgress } from '../forms/VendorAdditionalServicesFormSection';
import useStyles from '../../../../Settings/Account/marketplace/VendorConfig.styles';
import VendorConfigWrapper from './VendorConfigWrapper';
import VendorStatusBanner from './VendorStatusBanner';
import { Cloud } from 'utils/cloudTypes';

const ConfigureMsftCosell = ({
  onAfterCompleted,
}: {
  onAfterCompleted: (cloud: string) => void;
}) => {
  const { progress: firstSectionProgress } =
    useVendorMarketplaceAccountFormSectionProgress();
  const { progress: secondSectionProgress } =
    useVendorTackleAndMsftFormSectionProgress(true);
  const { progress: thirdSectionProgress } =
    useVendorAdditionalServicesFormSectionProgress();
  const classes = useStyles();

  const progress = sumFormProgressQueryResult(
    {
      progress: firstSectionProgress,
      loading: false,
      didError: false,
    },
    {
      progress: secondSectionProgress,
      loading: false,
      didError: false,
    },
    {
      progress: thirdSectionProgress,
      loading: false,
      didError: false,
    },
  );

  if (progress.progress.complete === progress.progress.total) {
    onAfterCompleted(Cloud.Azure);
  }

  return (
    <VendorConfigWrapper
      progress={progress}
      onAfterCompleted={onAfterCompleted}
      cloudString="Microsoft"
    >
      <VendorStatusBanner cloud={Cloud.Azure} />
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <div className={classes.banner}>
            <Banner
              title="Before you begin"
              body={
                <div className={classes.bannerContent}>
                  <Typography variant="body2">
                    Be sure you have the right skills and permissions to carry
                    out these tasks, or that you are working with someone at
                    your organization who does. If you need to get help from
                    others, you may want to schedule time to complete these
                    tasks together. You will need knowledge of and access to:
                  </Typography>
                  <ul>
                    <li>
                      <b>Your Microsoft Partner Center account</b>
                      <br />
                      You must have a Microsoft Partner Center account with a{' '}
                      <Link
                        showExternalIcon
                        external
                        to="https://docs.microsoft.com/en-us/partner-center/partner-center-account-setup#locate-your-mpn-id"
                        className={classes.link}
                      >
                        Microsoft AI Cloud Partner Program ID
                      </Link>
                      . If you don't, you must enroll in the{' '}
                      <Link
                        showExternalIcon
                        external
                        to="https://docs.microsoft.com/en-us/partner-center/mpn-create-a-partner-center-account"
                        className={classes.link}
                      >
                        Microsoft Partner Center Program
                      </Link>
                      . If you've previously enrolled, be sure you've accepted
                      the latest agreements.
                      <br />
                      To complete these steps, you will need Owner or Manager,
                      and Finance Contributor role access in your Partner Center
                      account. Refer to Microsoft Documentation on{' '}
                      <Link
                        showExternalIcon
                        external
                        to="https://docs.microsoft.com/en-us/azure/marketplace/add-manage-users"
                        className={classes.link}
                      >
                        Adding and Managing Users
                      </Link>{' '}
                      if you need help with this. You can also check with your
                      Microsoft Business Development rep if you have one, or
                      submit a ticket in the Partner Center.
                    </li>
                    <li>
                      <b>Your instance of Azure Active Directory</b>
                      <br />
                      You will need Global Admin permissions.
                    </li>
                    <li>
                      <b>Tax and payment information</b>
                      <br />
                      You will need to know how your business handles taxes,
                      finances and payouts.
                    </li>
                  </ul>
                </div>
              }
            />
          </div>
        </Grid>
        <Grid item>
          <VendorMarketplaceAccountFormSection
            icon={<NumberIcon index={1} />}
          />
        </Grid>
        <Grid item>
          <VendorTackleAndMsftFormSection icon={<NumberIcon index={2} />} />
        </Grid>
        <Grid item>
          <VendorAdditionalServicesFormSection
            icon={<NumberIcon index={3} />}
          />
        </Grid>
      </Grid>
    </VendorConfigWrapper>
  );
};

export default ConfigureMsftCosell;
